import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Plus,
  Upload,
  Loader2,
  Search,
  Trash2,
  Edit,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import barStockQuoteService from '../services/barStockQuoteService';

const BarStockQuotesPage = () => {
  const navigate = useNavigate();

  // Date-first view states
  const [quoteDates, setQuoteDates] = useState([]);
  const [dateDetails, setDateDetails] = useState({}); // keyed by dateKey -> { items, loading }
  const [expandedDateKey, setExpandedDateKey] = useState(null);
  const [loadingDates, setLoadingDates] = useState(true);

  const [error, setError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importQuoteDate, setImportQuoteDate] = useState('');

  const [filters, setFilters] = useState({
    diameter_min: '',
    diameter_max: '',
    supplier_name: '',
  });

  const isEngineerOrAdmin = ['admin', 'engineer'].includes(
    authService.getCurrentUser()?.role
  );

  const fetchQuoteDates = useCallback(async () => {
    setLoadingDates(true);
    try {
      const params = {};
      if (filters.diameter_min) params.diameter_min = parseFloat(filters.diameter_min);
      if (filters.diameter_max) params.diameter_max = parseFloat(filters.diameter_max);
      if (filters.supplier_name) params.supplier_name = filters.supplier_name;

      const response = await barStockQuoteService.getQuoteDates(params);
      setQuoteDates(response.items || []);
    } catch (err) {
      console.error('Failed to fetch quote dates:', err);
      setError('获取报价日期失败');
    } finally {
      setLoadingDates(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchQuoteDates();
  }, [fetchQuoteDates]);

  const fetchDateDetails = useCallback(async (quoteDate) => {
    const dateKey = quoteDate;
    setDateDetails(prev => ({
      ...prev,
      [dateKey]: { items: prev[dateKey]?.items || [], loading: true }
    }));

    try {
      const params = {
        quote_date: quoteDate,
        page_size: 200,
      };
      const response = await barStockQuoteService.getQuotes(params);
      setDateDetails(prev => ({
        ...prev,
        [dateKey]: { items: response.items || [], loading: false }
      }));
    } catch (err) {
      console.error('Failed to fetch date details:', err);
      setDateDetails(prev => ({
        ...prev,
        [dateKey]: { items: [], loading: false }
      }));
    }
  }, []);

  const toggleDateExpanded = (quoteDate) => {
    const dateKey = quoteDate;
    if (expandedDateKey === dateKey) {
      setExpandedDateKey(null);
      return;
    }
    setExpandedDateKey(dateKey);
    if (!dateDetails[dateKey] || dateDetails[dateKey]?.items?.length === 0) {
      fetchDateDetails(quoteDate);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadLoading(true);
    try {
      const result = await barStockQuoteService.uploadQuoteFile(selectedFile);
      setExtractedData(result);
      setImportQuoteDate(result.quote_date || '');
    } catch (err) {
      console.error('Upload failed:', err);
      setError('文件上传失败');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleImport = async () => {
    if (!extractedData?.items?.length) return;

    try {
      const result = await barStockQuoteService.importQuotes(
        extractedData.items,
        replaceExisting,
        extractedData.supplier_name || '',
        importQuoteDate || extractedData.quote_date || ''
      );
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setExtractedData(null);
      setImportQuoteDate('');
      setReplaceExisting(false);
      setError('');

      const msg = `导入完成：新增 ${result.imported_count || 0} 条，更新 ${result.updated_count || 0} 条，跳过 ${result.skipped_count || 0} 条。`;
      if (result.skipped_count > 0) {
        setError(msg + ' 跳过的记录：同规格同供应商同报价日期已存在。如需覆盖，请勾选“替换同规格同供应商同报价日期的记录”。');
      } else {
        setError(msg);
      }

      fetchQuoteDates();
      setExpandedDateKey(null);
      setDateDetails({});
    } catch (err) {
      console.error('Import failed:', err);
      setError('导入失败');
    }
  };

  const handleDelete = async (id, quoteDate) => {
    if (!window.confirm('确定要删除这个报价吗？')) return;

    try {
      await barStockQuoteService.deleteQuote(id);
      fetchDateDetails(quoteDate);
      fetchQuoteDates();
    } catch (err) {
      console.error('Delete failed:', err);
      setError('删除失败');
    }
  };

  const handleDeleteByDate = async (quoteDate) => {
    if (!window.confirm(`确定要删除 ${formatDate(quoteDate)} 的全部报价吗？`)) return;

    try {
      const result = await barStockQuoteService.deleteByDate(quoteDate);
      setExpandedDateKey(null);
      fetchQuoteDates();
      setError(`已删除 ${formatDate(quoteDate)} 的 ${result.deleted_count || 0} 条报价`);
    } catch (err) {
      console.error('Delete by date failed:', err);
      setError('按日期删除失败');
    }
  };

  const formatPrice = (price) => {
    return price?.toFixed(2) || '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">棒料报价管理</h1>
          <div className="flex gap-2">
            {isEngineerOrAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setUploadDialogOpen(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  上传报价
                </Button>
                <Button onClick={() => navigate('/bar-stock-quotes/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  手动添加
                </Button>
              </>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>最小直径 (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={filters.diameter_min}
                  onChange={(e) => {
                    setFilters({ ...filters, diameter_min: e.target.value });
                  }}
                  placeholder="2.5"
                />
              </div>
              <div>
                <Label>最大直径 (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={filters.diameter_max}
                  onChange={(e) => {
                    setFilters({ ...filters, diameter_max: e.target.value });
                  }}
                  placeholder="20.0"
                />
              </div>
              <div>
                <Label>供应商</Label>
                <Input
                  value={filters.supplier_name}
                  onChange={(e) => {
                    setFilters({ ...filters, supplier_name: e.target.value });
                  }}
                  placeholder="供应商名称"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchQuoteDates} className="w-full">
                  <Search className="w-4 h-4 mr-2" />
                  搜索
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote Date Groups */}
        <div className="space-y-4">
          {loadingDates ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : quoteDates.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                暂无报价记录
              </CardContent>
            </Card>
          ) : (
            quoteDates.map((group) => {
              const dateKey = group.quote_date;
              const isExpanded = expandedDateKey === dateKey;
              const details = dateDetails[dateKey];

              return (
                <Card key={dateKey} className={isExpanded ? 'border-blue-300' : ''}>
                  <CardContent className="p-0">
                    <div
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleDateExpanded(group.quote_date)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-blue-100 text-blue-700 p-2 rounded-lg">
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-lg">
                            {formatDate(group.quote_date)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {group.count} 条记录 · {group.supplier_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm text-gray-500">
                          <p>直径范围：{group.min_diameter} ~ {group.max_diameter} mm</p>
                          {group.specifications?.length > 0 && (
                            <p className="truncate max-w-xs">
                              {group.specifications.join(', ')}
                            </p>
                          )}
                        </div>
                        {isEngineerOrAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteByDate(group.quote_date);
                            }}
                            title="删除该日期全部报价"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t">
                        {details?.loading ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin" />
                          </div>
                        ) : (
                          <div className="overflow-x-auto p-4">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-2 px-3">规格</th>
                                  <th className="text-left py-2 px-3">直径 (mm)</th>
                                  <th className="text-left py-2 px-3">长度 (mm)</th>
                                  <th className="text-right py-2 px-3">含税单价</th>
                                  <th className="text-right py-2 px-3">未税单价</th>
                                  <th className="text-right py-2 px-3">每毫米单价</th>
                                  <th className="text-left py-2 px-3">供应商</th>
                                  {isEngineerOrAdmin && (
                                    <th className="text-center py-2 px-3">操作</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(details?.items || []).map((quote) => (
                                  <tr key={quote._id} className="border-b hover:bg-gray-50">
                                    <td className="py-2 px-3">{quote.specification}</td>
                                    <td className="py-2 px-3">{quote.diameter}</td>
                                    <td className="py-2 px-3">{quote.length || 310}</td>
                                    <td className="py-2 px-3 text-right">
                                      ¥{formatPrice(quote.unit_price_tax_included)}
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                      ¥{formatPrice(quote.unit_price_tax_excluded)}
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                      ¥{formatPrice(quote.price_per_mm)}
                                    </td>
                                    <td className="py-2 px-3">{quote.supplier_name || '未知供应商'}</td>
                                    {isEngineerOrAdmin && (
                                      <td className="py-2 px-3 text-center">
                                        <div className="flex justify-center gap-2">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              navigate(`/bar-stock-quotes/${quote._id}/edit`)
                                            }
                                          >
                                            <Edit className="w-4 h-4" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              handleDelete(quote._id, group.quote_date)
                                            }
                                          >
                                            <Trash2 className="w-4 h-4 text-red-500" />
                                          </Button>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Upload Dialog */}
        {uploadDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl mx-4">
              <CardHeader>
                <CardTitle>上传报价文件</CardTitle>
              </CardHeader>
              <CardContent>
                {!extractedData ? (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-600 mb-4">
                        支持 Excel (.xlsx, .xls) 或 PDF 文件
                      </p>
                      <Input
                        type="file"
                        accept=".xlsx,.xls,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="quote-file"
                      />
                      <Label htmlFor="quote-file" className="cursor-pointer">
                        <Button variant="outline" asChild>
                          <span>选择文件</span>
                        </Button>
                      </Label>
                      {selectedFile && (
                        <p className="mt-2 text-sm text-gray-600">
                          已选择: {selectedFile.name}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setUploadDialogOpen(false);
                          setSelectedFile(null);
                        }}
                      >
                        取消
                      </Button>
                      <Button
                        onClick={handleUpload}
                        disabled={!selectedFile || uploadLoading}
                      >
                        {uploadLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            处理中...
                          </>
                        ) : (
                          '上传并提取'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-green-600">
                      成功提取 {extractedData.items?.length || 0} 条报价记录
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>报价日期</Label>
                        <Input
                          type="date"
                          value={importQuoteDate}
                          onChange={(e) => setImportQuoteDate(e.target.value)}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          可修改，作为本次导入的“一套价格”标识
                        </p>
                      </div>
                      <div>
                        <Label>供应商</Label>
                        <Input
                          value={extractedData.supplier_name || ''}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto border rounded">
                      <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left py-2 px-3">规格</th>
                            <th className="text-right py-2 px-3">单价</th>
                            <th className="text-left py-2 px-3">供应商</th>
                          </tr>
                        </thead>
                        <tbody>
                          {extractedData.items?.map((item, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-2 px-3">{item.specification}</td>
                              <td className="py-2 px-3 text-right">
                                ¥{item.unit_price_tax_included}
                              </td>
                              <td className="py-2 px-3">{item.supplier_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center space-x-2 py-2">
                      <Checkbox
                        id="replace-existing"
                        checked={replaceExisting}
                        onCheckedChange={(checked) => setReplaceExisting(checked)}
                      />
                      <Label htmlFor="replace-existing" className="text-sm cursor-pointer">
                        替换同规格、同供应商、同报价日期的记录（不勾选则作为新的一套价格插入）
                      </Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setExtractedData(null);
                          setSelectedFile(null);
                          setReplaceExisting(false);
                        }}
                      >
                        重新上传
                      </Button>
                      <Button onClick={handleImport}>确认导入</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BarStockQuotesPage;
